import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TourState, TourContextValue } from './types';
import { tourSteps } from './tourSteps';

const TOUR_STORAGE_KEY = 'daydrive_tour_completed';

const initialState: TourState = {
  isActive: false,
  currentStep: 0,
  hasCompletedTour: false,
};

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TourState>(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    return {
      ...initialState,
      hasCompletedTour: completed === 'true',
    };
  });

  const startTour = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: true,
      currentStep: 0,
    }));
  }, []);

  const endTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setState(prev => ({
      ...prev,
      isActive: false,
      hasCompletedTour: true,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const nextStepIndex = prev.currentStep + 1;
      if (nextStepIndex >= tourSteps.length) {
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        return {
          ...prev,
          isActive: false,
          hasCompletedTour: true,
        };
      }
      return {
        ...prev,
        currentStep: nextStepIndex,
      };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const goToStep = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(0, Math.min(step, tourSteps.length - 1)),
    }));
  }, []);

  const skipTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setState(prev => ({
      ...prev,
      isActive: false,
      hasCompletedTour: true,
    }));
  }, []);

  // Auto-start tour for new users (desktop only — mobile UI is too different)
  useEffect(() => {
    if (!state.hasCompletedTour && !state.isActive) {
      const timer = setTimeout(() => {
        // Only auto-start on desktop (lg breakpoint = 1024px)
        if (window.innerWidth >= 1024) {
          startTour();
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state.hasCompletedTour, state.isActive, startTour]);

  return (
    <TourContext.Provider
      value={{
        state,
        startTour,
        endTour,
        nextStep,
        prevStep,
        goToStep,
        skipTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

// Helper to reset tour (for testing or settings)
export function resetTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  window.location.reload();
}
